import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useContactGroups } from './hooks/useContactGroups';
import { GroupInvoiceModal } from './GroupInvoiceModal';

/**
 * GroupInvoicePicker — lets the user pick a contact group, then hands off to
 * GroupInvoiceModal to generate the consolidated group invoice.
 *
 * - From the billing list (entry point B): no contactId → all groups listed.
 * - From the invoice form (entry point C): pass contactId → only groups that
 *   contain that client; auto-selects when there's a single match.
 *
 * Visibility/permission gating is the caller's responsibility
 * (group_management.manage_invoicing).
 */
export function GroupInvoicePicker({
  contactId,
  onClose,
}: {
  contactId?: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useContactGroups(contactId ? { contact_id: contactId } : {});
  const groups = data?.groups ?? [];
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [confirmedGroupId, setConfirmedGroupId] = useState('');

  // Auto-advance when exactly one group is available (common for the invoice
  // form flow where a client typically belongs to a single group).
  useEffect(() => {
    if (!confirmedGroupId && groups.length === 1) {
      setConfirmedGroupId(groups[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups.length]);

  const confirmedGroup = groups.find((g) => g.id === confirmedGroupId);

  // Once a group is confirmed, defer entirely to the group invoice modal.
  if (confirmedGroup) {
    return (
      <GroupInvoiceModal
        groupId={confirmedGroup.id}
        hasPrimary={!!confirmedGroup.primary_contact_id}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="New Group Invoice"
      description="Pick a group to bill. One consolidated invoice will be created for the group's primary/billing contact."
      size="md"
    >
      <div className="space-y-4">
        {isLoading ? (
          <p className="py-4 text-center text-sm text-gray-500">Loading groups...</p>
        ) : groups.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
            {contactId
              ? "This client isn't part of any group."
              : 'No contact groups found. Create a group first (Groups screen).'}
          </p>
        ) : (
          <Select
            label="Group"
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            placeholder="Select a group"
            options={groups.map((g) => ({
              value: g.id,
              label: g.primary_contact_name
                ? `${g.name} — bills to ${g.primary_contact_name}`
                : `${g.name} (no billing contact set)`,
            }))}
          />
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!selectedGroupId}
            onClick={() => setConfirmedGroupId(selectedGroupId)}
          >
            Continue
          </Button>
        </div>
      </div>
    </Modal>
  );
}
